/**
 * 受信依頼 — PIC inbox inside the 部品受注入力 app.
 * PICs (部署管理 → 部品依頼の通知先 assignees; admins see all) view every
 * user-submitted request, print the A4 発注依頼書, change status, and track
 * Techs 転記. Works fully while the 資材 module is paused.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Package, X, CheckCircle, Clock, PaperPlaneTilt, Printer, ArrowRight,
  Article, Link as LinkIcon, FilePdf, CheckSquare, Square, User as UserIcon, MagnifyingGlass,
  Eye, PencilSimple, Trash, Copy, Barcode, FileXls, Desktop,
} from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StorageThumb, StorageLink } from '@/components/StorageMedia';
import LabelPrintDialog from '@/components/labels/LabelPrintDialog';
import { buildPartsReqLabel } from '@/components/labels/labelSizes';
import ArrivalConfirmDialog from './ArrivalConfirmDialog';
import RegisterAsAssetDialog from './RegisterAsAssetDialog';
import ProcurementDialog, { PROCUREMENT_META } from './ProcurementDialog';
import { DeliveryDestPicker } from './partsRequest/DeliveryDestPicker';
import { shizaiApi } from './shizaiApi';
import { todayJst, jstDate } from '@/utils/jstDate';

const STATUS_META = {
  open:      { label: '未処理', badge: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  ordered:   { label: '発注済', badge: 'bg-sky-50 text-sky-700 border-sky-200', Icon: PaperPlaneTilt },
  partial:   { label: '一部入荷', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', Icon: Package },
  received:  { label: '入荷済', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle },
  cancelled: { label: '取消', badge: 'bg-zinc-100 text-zinc-500 border-zinc-200', Icon: X },
};
const PRIORITY_META = {
  low:    { label: '低',   badge: 'bg-zinc-100 text-zinc-600 border-zinc-200' },
  normal: { label: '通常', badge: 'bg-sky-50 text-sky-700 border-sky-200' },
  high:   { label: '高',   badge: 'bg-rose-50 text-rose-700 border-rose-200' },
};

export default function PicRequests({ categories, onEdit, initialQuery = '' }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ddFilter, setDdFilter] = useState('all');
  const [techsOnly, setTechsOnly] = useState(false);
  const [q, setQ] = useState(initialQuery);

  const catMap = useMemo(() => Object.fromEntries((categories || []).map((c) => [c.id, c])), [categories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await shizaiApi.picRequests());
    } catch (e) {
      toast.error(e?.response?.data?.detail || '受信依頼の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const ddOptions = useMemo(() => {
    const m = new Map();
    requests.forEach((r) => { if (r.delivery_destination?.id) m.set(r.delivery_destination.id, r.delivery_destination.name); });
    return [...m.entries()];
  }, [requests]);

  const rows = requests
    .filter((r) => statusFilter === 'all' || r.status === statusFilter)
    .filter((r) => !techsOnly || !r.techs_entered)
    .filter((r) => ddFilter === 'all' || r.delivery_destination?.id === ddFilter)
    .filter((r) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      const hay = [
        r.request_no, r.techs_order_no, r.requested_by, r.order_name,
        r.department_name, r.order_category_name,
        r.delivery_destination?.code, r.delivery_destination?.name, r.delivery_destination?.formal_name,
        ...(r.lines || []).flatMap((ln) => [ln.name, ln.part_no, ln.maker]),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(s);
    });

  const openBatchPrint = () => {
    if (rows.length === 0) return;
    const ids = rows.map((r) => r.id).join(',');
    window.open(`/shizai/requests-print-batch?ids=${ids}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4" data-testid="pic-requests">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="検索 — 依頼No / Techs受注No / 品名 / 依頼者 / 受注名..."
          className="pl-9 h-10 bg-white"
          data-testid="pic-search"
        />
        {q && (
          <button type="button" onClick={() => setQ('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            data-testid="pic-search-clear">
            <X size={14} weight="bold" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'open', 'ordered', 'partial', 'received', 'cancelled'].map((k) => {
          const count = k === 'all' ? requests.length : requests.filter((r) => r.status === k).length;
          const active = statusFilter === k;
          return (
            <button key={k} onClick={() => setStatusFilter(k)} data-testid={`pic-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                active ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              }`}>
              {k === 'all' ? 'すべて' : STATUS_META[k]?.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] px-1 h-4 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-zinc-100 text-zinc-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
        <button onClick={() => setTechsOnly((v) => !v)} data-testid="pic-filter-techs"
          className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1.5 ${
            techsOnly ? 'bg-emerald-600 text-white' : 'bg-white border border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50'
          }`}>
          Techs未入力のみ
        </button>
        {ddOptions.length > 0 && (
          <select value={ddFilter} onChange={(e) => setDdFilter(e.target.value)}
            className="h-8 rounded-full border border-teal-200 bg-white px-2.5 text-xs text-teal-700 outline-none"
            data-testid="pic-dd-filter">
            <option value="all">納入先: すべて</option>
            {ddOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-zinc-400">{rows.length}件</span>
          <Button size="sm" variant="outline" onClick={openBatchPrint} disabled={rows.length === 0}
            className="h-8 text-indigo-600 border-indigo-200 hover:bg-indigo-50"
            data-testid="pic-batch-print"
            title="表示中の依頼を1件ずつA4で連続印刷します">
            <Printer size={14} className="mr-1" /> まとめて印刷
          </Button>
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center" data-testid="pic-empty">
          <Article size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">該当する依頼はありません</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pic-list">
          {rows.map((r) => <PicCard key={r.id} req={r} catMap={catMap} onChanged={load} onEdit={onEdit} />)}
        </div>
      )}
    </div>
  );
}

function PicCard({ req, catMap, onChanged, onEdit }) {
  const status = STATUS_META[req.status] || STATUS_META.open;
  const priority = PRIORITY_META[req.priority] || PRIORITY_META.normal;
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [techsDialog, setTechsDialog] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [arrivalOpen, setArrivalOpen] = useState(false);
  // C. 入荷登録 — 数量Nの明細を1台=1行で台帳（IT資産/備品）へ入れる確認画面
  const [assetReg, setAssetReg] = useState(null);
  const [assetRegBusy, setAssetRegBusy] = useState(false);

  const openAssetRegistration = async () => {
    setAssetRegBusy(true);
    try {
      const p = await shizaiApi.arrivalPreview(req.id);
      const ledger = p.target_master === 'office' ? 'office' : 'it';
      setAssetReg({
        ledger,
        order: {
          id: req.id,
          request_no: req.request_no,
          order_no: req.request_no,
          received_at: jstDate(req.arrival_confirmation?.invoice_date
            || req.arrival_confirmation?.confirmed_at) || todayJst(),
          supplier_name: req.arrival_confirmation?.supplier_name
            || req.procurement?.supplier_name || null,
          lines: (p.lines || []).map((ln) => ({
            name: ln.name,
            part_no: ln.part_no,
            qty: ln.qty,
            uom: ln.uom,
            unit_price: ln.unit_price || 0,
            amount: ln.received_amount || ln.estimated_total || 0,
          })),
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || '入荷登録の準備に失敗しました');
    } finally { setAssetRegBusy(false); }
  };
  const [procOpen, setProcOpen] = useState(false);
  const [ddOpen, setDdOpen] = useState(false);
  const [ddValue, setDdValue] = useState(null);
  const [orderNo, setOrderNo] = useState('');
  const cat = req.order_category_id ? (catMap[req.order_category_id] || null) : null;
  const catName = cat?.name || req.order_category_name;
  const procMethod = req.procurement?.method || 'undecided';
  const procMeta = PROCUREMENT_META[procMethod] || PROCUREMENT_META.undecided;

  const del = async () => {
    if (!window.confirm(`依頼 ${req.request_no} を削除しますか？この操作は取り消せません。`)) return;
    setBusy(true);
    try {
      await shizaiApi.cancelMyRequest(req.id);
      toast.success(`依頼 ${req.request_no} を削除しました`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '削除に失敗しました');
    } finally { setBusy(false); }
  };

  const saveDd = async () => {
    setBusy(true);
    try {
      await shizaiApi.patchPicDelivery(req.id, ddValue?.id || null);
      toast.success(ddValue ? `納入先を「${ddValue.name}」に設定しました` : '納入先を解除しました');
      setDdOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '納入先の保存に失敗しました');
    } finally { setBusy(false); }
  };

  const [voidAsk, setVoidAsk] = useState(null);

  const setStatus = async (v) => {
    if (v === req.status) return;
    if (v === 'received') {
      // 入荷済 → 入荷確認モーダル（実際購入金額の記録 + マスター原価反映）
      setArrivalOpen(true);
      return;
    }
    await applyStatus(v, null);
  };

  // 入荷済/一部入荷 を戻すときは、購入記録を取り消すかどうかを先に聞く
  const applyStatus = async (v, voidRecords) => {
    setBusy(true);
    try {
      const r = await shizaiApi.patchPicStatus(req.id, v, voidRecords);
      toast.success(`ステータスを「${STATUS_META[v]?.label}」に変更しました`
        + (r.voided_purchase_records ? `（購入記録 ${r.voided_purchase_records}件 を取り消し）` : ''));
      setVoidAsk(null);
      onChanged();
    } catch (e) {
      // サーバーのオブジェクト詳細は共通インターセプターが detail_obj に退避している
      const d = e?.response?.data?.detail_obj || e?.response?.data?.detail;
      if (d && d.confirm_required === 'void_purchase_records') {
        setVoidAsk({ status: v, message: d.message, detail: d.detail, records: d.records });
      } else {
        toast.error(typeof d === 'string' ? d : '更新に失敗しました');
      }
    } finally { setBusy(false); }
  };

  const openTechsDialog = () => {
    setOrderNo(req.techs_order_no || '');
    setTechsDialog(true);
  };

  const downloadTechsExcel = async () => {
    try {
      const blob = await shizaiApi.techsExport(req.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `部品表入力_${req.request_no}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('TECHS部品表 Excel をダウンロードしました');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'エクスポートに失敗しました');
    }
  };

  const saveTechs = async () => {
    setBusy(true);
    try {
      await shizaiApi.patchPicTechs(req.id, true, orderNo.trim());
      toast.success(orderNo.trim() ? `Techs入力済み（受注番号: ${orderNo.trim()}）にしました` : 'Techs入力済みにしました');
      setTechsDialog(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '更新に失敗しました');
    } finally { setBusy(false); }
  };

  const toggleTechs = async () => {
    if (!req.techs_entered) {
      // Ask the PIC for the Techs-side 受注番号 before marking as entered.
      openTechsDialog();
      return;
    }
    setBusy(true);
    try {
      await shizaiApi.patchPicTechs(req.id, false);
      toast.success('Techs未入力に戻しました');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '更新に失敗しました');
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`pic-card-${req.id}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <button onClick={() => setExpanded(!expanded)}
          className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${status.badge.replace('border-', 'ring-1 ring-')}`}
          data-testid={`pic-expand-${req.id}`}>
          <status.Icon size={16} weight="duotone" />
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-zinc-500">{req.request_no}</span>
            <Badge variant="outline" className={status.badge}>{status.label}</Badge>
            <Badge variant="outline" className={priority.badge}>優先度: {priority.label}</Badge>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setProcOpen(true); }}
              className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold transition-colors hover:opacity-80 ${procMeta.chip}`}
              data-testid={`pic-procurement-${req.id}`}
              title={`調達方法: ${procMeta.label}（クリックで変更）`}>
              調達: {procMeta.label}
            </button>
            <button type="button"
              onClick={(e) => { e.stopPropagation(); setDdValue(req.delivery_destination?.id ? req.delivery_destination : null); setDdOpen(true); }}
              className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold transition-colors hover:opacity-80 ${
                req.delivery_destination?.name
                  ? 'bg-teal-50 text-teal-700 border-teal-200'
                  : 'bg-zinc-100 text-zinc-500 border-dashed border-zinc-300'}`}
              data-testid={`pic-dd-${req.id}`}
              title={req.delivery_destination?.formal_name
                ? `納入先: ${req.delivery_destination.formal_name}（クリックで変更）`
                : '納入先: 未設定（クリックで設定 — 発注前に必須）'}>
              納入先: {req.delivery_destination?.name || '未設定'}
            </button>
            {req.notify_group_name && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200"
                data-testid={`pic-group-${req.id}`}>
                {req.notify_group_name}
              </span>
            )}
            {catName && (
              <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[10px]">
                {catName}{cat?.symbol ? ` (${cat.symbol})` : ''}
              </span>
            )}
            {req.order_name && (
              <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 text-[10px]">
                受注: {req.order_name}
              </span>
            )}
            {req.techs_entered && (
              <button type="button" onClick={openTechsDialog}
                className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 text-[10px] font-semibold transition-colors"
                data-testid={`pic-techs-badge-${req.id}`}
                title={`Techs入力済み（${req.techs_entered_by || ''} ${String(req.techs_entered_at || '').slice(0, 10)}）— クリックで受注番号を編集`}>
                Techs入力済{req.techs_order_no ? ` · 受注No ${req.techs_order_no}` : ''}
              </button>
            )}
            <span className="text-xs text-zinc-400">{String(req.created_at || '').slice(0, 10)}</span>
          </div>
          <div className="mt-1 text-sm font-medium text-zinc-900 truncate">
            {req.first_line_name || req.lines?.[0]?.name || '(no items)'}
            {(req.lines?.length || 0) > 1 && <span className="ml-2 text-xs text-zinc-500">+{req.lines.length - 1}件</span>}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
            {req.requested_by && (
              <span className="inline-flex items-center gap-1" data-testid={`pic-requester-${req.id}`}>
                <UserIcon size={11} weight="bold" /> {req.requested_by}
              </span>
            )}
            {req.needed_by && <span>希望納期: {req.needed_by}</span>}
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* 発注までの流れ順: ①調達方法 → ②納入先 → ③Techs → ④ステータス */}
          <div className="flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50/70 p-1"
            data-testid={`pic-flow-strip-${req.id}`}
            title="発注までの流れ: ①調達方法 → ②納入先 → ③Techs入力 → ④ステータス変更">
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => setProcOpen(true)}
              className={`h-7 px-1.5 ${(req.procurement?.method || 'undecided') !== 'undecided'
                ? 'text-sky-700 border-sky-300 bg-sky-50 hover:bg-sky-100' : 'text-zinc-500 bg-white'}`}
              data-testid={`pic-proc-check-${req.id}`}
              title={(req.procurement?.method || 'undecided') !== 'undecided'
                ? `①調達方法設定済み（${procMeta.label}）— クリックで変更`
                : '①クリックで調達方法を選択（発注前に必須）'}>
              {(req.procurement?.method || 'undecided') !== 'undecided'
                ? <CheckSquare size={13} weight="fill" /> : <Square size={13} />}
              <span className="ml-0.5 text-[10px] font-semibold">①調達</span>
            </Button>
            <Button size="sm" variant="outline" disabled={busy}
              onClick={() => { setDdValue(req.delivery_destination?.id ? req.delivery_destination : null); setDdOpen(true); }}
              className={`h-7 px-1.5 ${req.delivery_destination?.id
                ? 'text-teal-700 border-teal-300 bg-teal-50 hover:bg-teal-100' : 'text-zinc-500 bg-white'}`}
              data-testid={`pic-dd-check-${req.id}`}
              title={req.delivery_destination?.id
                ? `②納入先設定済み（${req.delivery_destination.name}）— クリックで変更`
                : '②クリックで納入先を選択（発注前に必須）'}>
              {req.delivery_destination?.id
                ? <CheckSquare size={13} weight="fill" /> : <Square size={13} />}
              <span className="ml-0.5 text-[10px] font-semibold">②納入先</span>
            </Button>
            {/* 直接購入の場合は Techs 入力ステップ不要 */}
            {(req.procurement?.method || 'undecided') !== 'direct' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={toggleTechs}
                className={`h-7 px-1.5 ${req.techs_entered ? 'text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100' : 'text-zinc-500 bg-white'}`}
                data-testid={`pic-techs-${req.id}`}
                title={req.techs_entered ? '③Techs入力済み — クリックで未入力に戻す' : '③クリックで Techs入力済み にする'}>
                {req.techs_entered ? <CheckSquare size={13} weight="fill" /> : <Square size={13} />}
                <span className="ml-0.5 text-[10px] font-semibold">③Techs</span>
              </Button>
            )}
            {/* TECHS 部品表インポート用 Excel（調達方法=TECHS のときのみ） */}
            {req.procurement?.method === 'techs' && (
              <Button size="sm" variant="outline" disabled={busy} onClick={downloadTechsExcel}
                className="h-7 px-1.5 text-green-700 border-green-300 bg-green-50 hover:bg-green-100"
                data-testid={`pic-techs-excel-${req.id}`}
                title="TECHS部品表インポート用の Excel をダウンロード">
                <FileXls size={13} weight="fill" />
                <span className="ml-0.5 text-[10px] font-semibold">Excel</span>
              </Button>
            )}
            {(req.status === 'received' || req.status === 'partial') && (
              <Button size="sm" variant="outline" disabled={busy || assetRegBusy}
                onClick={openAssetRegistration}
                className="h-7 px-1.5 text-indigo-700 border-indigo-300 bg-indigo-50 hover:bg-indigo-100"
                data-testid={`pic-asset-register-${req.id}`}
                title="入荷登録 — 数量の分だけ台帳（IT資産/備品）に行を作る">
                <Desktop size={13} weight="fill" />
                <span className="ml-0.5 text-[10px] font-semibold">入荷登録</span>
              </Button>
            )}
            <Select value={req.status} onValueChange={setStatus} disabled={busy}>
              <SelectTrigger className="h-7 w-[104px] text-xs bg-white" data-testid={`pic-status-${req.id}`}
                title="④ステータス変更（発注済 → 一部入荷 → 入荷済）">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span className="w-px h-7 bg-zinc-200" />
          <Button size="sm" variant="outline" className="h-8 px-2 text-indigo-600 hover:bg-indigo-50"
            onClick={() => window.open(`/shizai/requests/${req.id}/print`, '_blank', 'noopener,noreferrer')}
            data-testid={`pic-print-${req.id}`} title="発注依頼書をA4で印刷">
            <Printer size={14} />
          </Button>
          {(req.techs_entered || ['ordered', 'partial', 'received'].includes(req.status)) && (
            <Button size="sm" variant="outline" className="h-8 px-2 text-orange-600 hover:bg-orange-50"
              onClick={() => setLabelOpen(true)}
              data-testid={`pic-label-${req.id}`} title="バーコードラベルを印刷（REQ番号）">
              <Barcode size={14} />
            </Button>
          )}
          <Button size="sm" variant="outline" className="h-8 px-2 text-zinc-600 hover:bg-zinc-50"
            onClick={() => setDetailOpen(true)}
            data-testid={`pic-detail-open-${req.id}`} title="詳細を開く（各項目をコピーできます）">
            <Eye size={14} />
          </Button>
          <Button size="sm" variant="outline" className="h-8 px-2 text-sky-600 hover:bg-sky-50"
            onClick={() => onEdit?.(req)} disabled={busy}
            data-testid={`pic-edit-${req.id}`} title="依頼を編集">
            <PencilSimple size={14} />
          </Button>
          <Button size="sm" variant="outline" className="h-8 px-2 text-rose-600 hover:bg-rose-50"
            onClick={del} disabled={busy}
            data-testid={`pic-delete-${req.id}`} title="依頼を削除">
            <Trash size={14} />
          </Button>
          <ArrowRight size={14} weight="bold"
            className={`text-zinc-400 ml-1 transition-transform cursor-pointer ${expanded ? 'rotate-90' : ''}`}
            onClick={() => setExpanded(!expanded)} />
        </div>
      </div>

      {detailOpen && <PicDetailDialog req={req} catMap={catMap} onClose={() => setDetailOpen(false)} />}
      {assetReg && (
        <RegisterAsAssetDialog
          open
          order={assetReg.order}
          ledger={assetReg.ledger}
          onClose={() => setAssetReg(null)}
          onRegistered={() => { setAssetReg(null); onChanged(); }}
        />
      )}
      {arrivalOpen && (
        <ArrivalConfirmDialog
          req={req}
          onClose={() => setArrivalOpen(false)}
          onSaved={() => { setArrivalOpen(false); onChanged(); }}
        />
      )}
      {procOpen && (
        <ProcurementDialog
          req={req}
          onClose={() => setProcOpen(false)}
          onSaved={() => { setProcOpen(false); onChanged(); }}
        />
      )}
      {labelOpen && (
        <LabelPrintDialog open onClose={() => setLabelOpen(false)} labels={[buildPartsReqLabel(req)]} />
      )}
      <Dialog open={ddOpen} onOpenChange={(o) => !o && setDdOpen(false)}>
        <DialogContent className="max-w-sm overflow-visible" data-testid={`pic-dd-dialog-${req.id}`}>
          <DialogHeader>
            <DialogTitle className="text-base">
              納入先を設定 <span className="font-mono text-xs text-zinc-500">{req.request_no}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">「発注済」にする前に、調達方法と納入先の設定が必要です。</p>
          <DeliveryDestPicker value={ddValue} onChange={setDdValue} testId={`pic-dd-picker-${req.id}`} />
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setDdOpen(false)} data-testid={`pic-dd-cancel-${req.id}`}>
              キャンセル
            </Button>
            <Button size="sm" onClick={saveDd} disabled={busy}
              className="bg-teal-700 hover:bg-teal-800" data-testid={`pic-dd-save-${req.id}`}>
              保存
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {expanded && (
        <div className="border-t border-zinc-200 bg-zinc-50/50 px-4 py-3 space-y-2" data-testid={`pic-detail-${req.id}`}>
          {(req.lines || []).map((ln, i) => (
            <div key={`${req.id}-ln-${i}`} className="bg-white rounded-lg border border-zinc-200 p-3 flex items-start gap-3">
              {ln.photo_url && (
                ln.photo_is_pdf ? (
                  <StorageLink path={ln.photo_url} target="_blank" rel="noreferrer"
                    className="w-12 h-12 rounded bg-rose-50 border border-rose-200 flex flex-col items-center justify-center shrink-0 hover:bg-rose-100"
                    title={ln.photo_filename || 'PDFを開く'}>
                    <FilePdf size={18} weight="duotone" className="text-rose-600" />
                    <span className="text-[8px] font-bold text-rose-700 mt-0.5">PDF</span>
                  </StorageLink>
                ) : (
                  <StorageLink path={ln.photo_url} target="_blank" rel="noreferrer" title={ln.photo_filename || '画像を開く'}>
                    <StorageThumb path={ln.photo_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                  </StorageLink>
                )
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-800">{ln.name || ln.part_no}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                  {ln.part_no && <span className="font-mono">{ln.part_no}</span>}
                  {ln.drawing_no && <span className="text-zinc-400">図 {ln.drawing_no}</span>}
                  {ln.model_no && <span className="text-zinc-400">{ln.model_no}</span>}
                  {ln.maker && <span className="text-zinc-400">· {ln.maker}</span>}
                  <span className="tabular-nums font-semibold">{ln.qty} {ln.uom}</span>
                </div>
                {ln.reference_url && (
                  <a href={ln.reference_url} target="_blank" rel="noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 truncate max-w-full">
                    <LinkIcon size={11} weight="bold" /> {ln.reference_url}
                  </a>
                )}
                {ln.notes && <div className="text-xs text-zinc-500 mt-1">{ln.notes}</div>}
              </div>
            </div>
          ))}
          {req.notes && <div className="text-xs text-zinc-500 px-1">全体備考: {req.notes}</div>}
        </div>
      )}

      <Dialog open={!!voidAsk} onOpenChange={(o) => !o && setVoidAsk(null)}>
        <DialogContent className="max-w-sm" data-testid={`pic-void-records-dialog-${req.id}`}>
          <DialogHeader>
            <DialogTitle className="text-base">{voidAsk?.message || '入荷記録を取り消しますか？'}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-600">{voidAsk?.detail}</p>
          <p className="text-[11px] text-zinc-400">
            取り消しても記録は残ります（購入実績の集計から外れるだけ）。残す場合、次回の入荷確認でこの記録を更新します。
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" disabled={busy}
              onClick={() => applyStatus(voidAsk.status, false)}
              data-testid={`pic-void-records-keep-${req.id}`}>残す</Button>
            <Button size="sm" disabled={busy} className="bg-rose-700 hover:bg-rose-800"
              onClick={() => applyStatus(voidAsk.status, true)}
              data-testid={`pic-void-records-void-${req.id}`}>取り消す</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={techsDialog} onOpenChange={setTechsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base">
              {req.techs_entered ? 'Techs受注番号を編集' : 'Techs入力済みにする'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-zinc-500">
              Techsシステムに入力した<span className="font-semibold text-zinc-700">受注番号</span>を記録してください（後からバッジをクリックして編集できます）。
            </p>
            <Input
              value={orderNo}
              onChange={(e) => setOrderNo(e.target.value)}
              placeholder="例: 24-01234"
              maxLength={64}
              autoFocus
              data-testid={`pic-techs-orderno-${req.id}`}
              onKeyDown={(e) => { if (e.key === 'Enter' && !busy) saveTechs(); }}
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setTechsDialog(false)} data-testid={`pic-techs-cancel-${req.id}`}>
              キャンセル
            </Button>
            <Button size="sm" onClick={saveTechs} disabled={busy}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid={`pic-techs-save-${req.id}`}>
              {orderNo.trim() ? '登録' : '番号なしで登録'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CopyRow({ label, value, testId, href }) {
  if (value === null || value === undefined || value === '') return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      toast.success(`${label}をコピーしました`);
    } catch (_e) {
      toast.error('コピーに失敗しました');
    }
  };
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-zinc-200 last:border-b-0">
      <span className="w-28 shrink-0 text-[11px] font-semibold text-zinc-400">{label}</span>
      {href ? (
        <a href={href} target="_blank" rel="noreferrer"
          className="flex-1 min-w-0 text-sm text-sky-600 hover:text-sky-800 underline select-text break-all">
          {String(value)}
        </a>
      ) : (
        <span className="flex-1 min-w-0 text-sm text-zinc-800 select-text break-all">{String(value)}</span>
      )}
      <button
        type="button"
        onClick={copy}
        data-testid={testId}
        className="shrink-0 w-7 h-7 rounded-md border border-zinc-200 text-zinc-400 hover:text-sky-600 hover:border-sky-300 hover:bg-sky-50 flex items-center justify-center transition-colors"
        title={`${label}をコピー`}
      >
        <Copy size={13} />
      </button>
    </div>
  );
}

function PicDetailDialog({ req, catMap, onClose }) {
  const cat = req.order_category_id ? catMap?.[req.order_category_id] : null;
  const catName = cat?.name || req.order_category_name || '';
  const priorityLabel = (PRIORITY_META[req.priority] || PRIORITY_META.normal).label;
  const statusLabel = (STATUS_META[req.status] || STATUS_META.open).label;

  const lineTsv = (ln) => [
    ln.name || '', ln.part_no || '', ln.drawing_no || '', ln.maker || '', ln.maker_pn || '',
    ln.model_no || '', ln.qty ?? '', ln.uom || '', ln.reference_url || '', ln.notes || '',
  ].join('\t');

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText((req.lines || []).map(lineTsv).join('\n'));
      toast.success('全明細をコピーしました（Excel等に貼り付けできます）');
    } catch (_e) {
      toast.error('コピーに失敗しました');
    }
  };

  const copyLine = async (ln) => {
    try {
      await navigator.clipboard.writeText(lineTsv(ln));
      toast.success('明細をコピーしました');
    } catch (_e) {
      toast.error('コピーに失敗しました');
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pic-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-zinc-500">{req.request_no}</span>
            <span>依頼の詳細</span>
            <span className="text-xs font-normal text-zinc-400">各項目をコピーできます</span>
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 px-3 py-1" data-testid="pic-detail-header">
          <CopyRow label="依頼No" value={req.request_no} testId={`pic-copy-reqno-${req.id}`} />
          <CopyRow label="依頼者" value={req.requested_by} testId={`pic-copy-requester-${req.id}`} />
          <CopyRow label="状態" value={statusLabel} />
          <CopyRow label="優先度" value={priorityLabel} />
          <CopyRow label="希望納期" value={req.needed_by} testId={`pic-copy-needed-${req.id}`} />
          <CopyRow label="受注品名" value={req.order_name} testId={`pic-copy-ordername-${req.id}`} />
          <CopyRow label="受注分類" value={catName ? `${catName}${cat?.symbol ? ` (${cat.symbol})` : ''}` : ''} />
          <CopyRow label="作業指示製番" value={req.manufacturing_no} testId={`pic-copy-mno-${req.id}`} />
          <CopyRow label="Techs受注No" value={req.techs_order_no} testId={`pic-copy-techsno-${req.id}`} />
          <CopyRow label="調達方法" value={(PROCUREMENT_META[req.procurement?.method || 'undecided'] || PROCUREMENT_META.undecided).label} testId={`pic-copy-proc-${req.id}`} />
          <CopyRow label="納入先コード" value={req.delivery_destination?.code} testId={`pic-copy-ddcode-${req.id}`} />
          <CopyRow label="納入先" value={req.delivery_destination?.name} testId={`pic-copy-ddname-${req.id}`} />
          <CopyRow label="納入先正式名" value={req.delivery_destination?.formal_name} testId={`pic-copy-ddformal-${req.id}`} />
          <CopyRow label="注文先" value={req.procurement?.order_vendor} />
          <CopyRow label="注文番号" value={req.procurement?.order_no} />
          <CopyRow label="契約期間" value={req.procurement?.contract_period} />
          <CopyRow label="更新日" value={req.procurement?.renewal_date} />
          <CopyRow label="ライセンス数" value={req.procurement?.seat_count} />
          <CopyRow label="実際購入金額計" value={req.received_total_amount ? `¥${Number(req.received_total_amount).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}` : ''} testId={`pic-copy-actual-${req.id}`} />
          <CopyRow label="請求書番号" value={req.arrival_confirmation?.invoice_no || req.procurement?.invoice_no} testId={`pic-copy-invoice-${req.id}`} />
          <CopyRow label="購入先" value={req.arrival_confirmation?.supplier_name} />
          <CopyRow label="全体備考" value={req.notes} />
        </div>

        <div className="space-y-3">
          {(req.lines || []).map((ln, i) => (
            <div key={`${req.id}-dl-${i}`} className="rounded-lg border border-zinc-200 px-3 py-1" data-testid={`pic-detail-line-${i}`}>
              <div className="flex items-center justify-between pt-1.5">
                <span className="text-[11px] font-bold text-zinc-500">品目 {i + 1}</span>
                <div className="flex items-center gap-2">
                  {ln.photo_url && (
                    <StorageLink path={ln.photo_url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800"
                      data-testid={`pic-detail-attach-${i}`}>
                      {ln.photo_is_pdf ? <FilePdf size={13} weight="duotone" /> : <Package size={13} weight="duotone" />}
                      添付を開く
                    </StorageLink>
                  )}
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyLine(ln)}
                    data-testid={`pic-copy-line-${i}`}>
                    <Copy size={12} className="mr-1" /> この行をコピー
                  </Button>
                </div>
              </div>
              <CopyRow label="品名" value={ln.name} testId={`pic-copy-name-${i}`} />
              <CopyRow label="品番" value={ln.part_no} testId={`pic-copy-partno-${i}`} />
              <CopyRow label="図番" value={ln.drawing_no} />
              <CopyRow label="メーカー" value={ln.maker} />
              <CopyRow label="メーカー品番" value={ln.maker_pn} />
              <CopyRow label="型式" value={ln.model_no} />
              <CopyRow label="数量" value={`${ln.qty} ${ln.uom || ''}`.trim()} testId={`pic-copy-qty-${i}`} />
              <CopyRow label="参考URL" value={ln.reference_url} href={ln.reference_url} />
              <CopyRow label="備考" value={ln.notes} />
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={copyAll} data-testid={`pic-copy-all-${req.id}`}>
            <Copy size={14} className="mr-1.5" /> 全明細をコピー（タブ区切り）
          </Button>
          <Button variant="outline" onClick={onClose} data-testid="pic-detail-close">閉じる</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
